import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import { api } from "@/lib/api";
import useCaptcha from "@/hooks/useCaptcha";
import {
  AREAS_OF_EXPERTISE,
  CLIENT_TYPES,
  HOLDS_BUDGET_OPTIONS,
} from "@/lib/content";

// Static option lists mirroring backend
const SERVICE_AREA_OPTIONS = [
  ...AREAS_OF_EXPERTISE.map((a) => a.title),
  "Keynote Speaking",
  "Signature Workshop",
  "Not sure yet",
];

const BUDGET_RANGES = [
  "Under $2,500",
  "$2,500 to $5,000",
  "$5,000 to $10,000",
  "$10,000 to $25,000",
  "$25,000 to $50,000",
  "$50,000 plus",
  "Prefer to discuss",
];

const TIMELINES = [
  "As soon as possible",
  "Within the next 30 days",
  "Within the next 90 days",
  "This semester",
  "Next school year",
  "Flexible",
];

const REFERRAL_SOURCES = [
  "Referral",
  "Search engine",
  "Social media",
  "Press or article",
  "Speaking engagement",
  "Conference or event",
  "Other",
];

const INITIAL = {
  full_name: "",
  role_or_title: "",
  email: "",
  phone_number: "",
  client_type: "",
  organization_name: "",
  organization_link: "",
  service_area: "",
  needs_and_pain_points: "",
  scale_or_audience_size: "",
  timeline: "",
  budget_range: "Prefer to discuss",
  holds_budget: "",
  how_heard: "",
  how_heard_detail: "",
  website: "",
};

function audienceToClientType(a) {
  if (a === "schools") return null; // user must pick School or District
  if (a === "organizations") return null; // user picks Organization / Community / Company
  return null;
}

function StepShell({ step, total, title, description, children, onBack, onNext, nextLabel, nextDisabled, submitting }) {
  return (
    <div data-testid={`inquiry-step-${step}`} className="wt-card p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <SectionEyebrow>{`Step ${step} of ${total}`}</SectionEyebrow>
        <span className="text-[11px] tracking-[0.16em] uppercase wt-text-muted">
          Submit an Inquiry
        </span>
      </div>
      <h2 className="wt-h2 mt-4">{title}</h2>
      {description && <p className="wt-lead mt-3">{description}</p>}
      <div className="mt-7">{children}</div>
      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={!onBack}
          data-testid={`inquiry-back-${step}`}
          className="inline-flex items-center gap-1 text-sm wt-text-muted disabled:opacity-40"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled || submitting}
          data-testid={`inquiry-next-${step}`}
          className="wt-btn-primary h-11 px-6"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {nextLabel || "Continue"}
          {!submitting && <ArrowRight size={14} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

function LabelTag({ kind = "required" }) {
  return <span className={`wt-label-tag ${kind}`}>{kind === "required" ? "Required" : "Optional"}</span>;
}

export default function SubmitInquiryPage() {
  const [params] = useSearchParams();
  const presetService = params.get("service") || "";
  const presetAudience = params.get("audience") || "";

  const [form, setForm] = useState({
    ...INITIAL,
    service_area: SERVICE_AREA_OPTIONS.includes(presetService) ? presetService : "",
  });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const { captcha, reload } = useCaptcha();

  // Suggest client_type based on audience preset, but let the user choose.
  useEffect(() => {
    if (!form.client_type && presetAudience) {
      const hint = audienceToClientType(presetAudience);
      if (hint) setForm((f) => ({ ...f, client_type: hint }));
    }
  }, [presetAudience, form.client_type]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const TOTAL_STEPS = 7;

  const validStep = useMemo(() => {
    switch (step) {
      case 1:
        return form.full_name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
      case 2:
        return CLIENT_TYPES.includes(form.client_type) && form.organization_name.trim().length > 0;
      case 3:
        return Boolean(form.service_area) && form.needs_and_pain_points.trim().length >= 10;
      case 4:
        return true; // scale, timeline optional but fine
      case 5:
        return Boolean(form.budget_range);
      case 6:
        return true; // referral optional
      case 7:
        return captchaAnswer !== "" && !isNaN(Number(captchaAnswer));
      default:
        return false;
    }
  }, [step, form, captchaAnswer]);

  const goNext = () => {
    if (!validStep) {
      toast.error("Please complete the highlighted field before continuing.");
      return;
    }
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      onSubmit();
    }
  };

  const goBack = step > 1 ? () => setStep(step - 1) : null;

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        ...form,
        captcha_a: captcha.a,
        captcha_b: captcha.b,
        captcha_answer: Number(captchaAnswer),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(", ")
            : "Something went wrong. Please try again.",
      );
      setCaptchaAnswer("");
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="inquiry-page">
        <PageHeader
          eyebrow="Inquiry received"
          title="Thank you. Your inquiry has been received."
          lead="Wanakee reviews each submission personally and will follow up with the best next step if the work is aligned."
          testId="inquiry-confirmation-header"
        />
        <section className="pb-16">
          <div className="wt-container">
            <article
              data-testid="inquiry-confirmation"
              className="wt-card p-6 sm:p-8 max-w-2xl"
              style={{ borderLeft: "3px solid var(--wt-primary)" }}
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={22} className="mt-0.5" style={{ color: "var(--wt-primary)" }} />
                <div>
                  <p className="wt-eyebrow">Confirmation</p>
                  <p className="mt-2 wt-body">
                    Your submission was routed to the correct path inside the workspace. You will
                    hear back from Wanakee directly.
                  </p>
                </div>
              </div>
              <div className="mt-6 wt-hairline" />
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link to="/" className="wt-btn-outline">Back to home</Link>
                <Link to="/book-consultation" className="wt-btn-primary">Book a Consultation</Link>
              </div>
            </article>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="inquiry-page">
      <PageHeader
        eyebrow="Submit an Inquiry"
        title="Tell me about the work, one step at a time."
        lead="Each question helps me sort the inquiry correctly and prepare for our conversation. Budget is captured here, not in the consultation flow."
        testId="inquiry-page-header"
      />

      <section className="pb-16">
        <div className="wt-container">
          <div className="max-w-2xl">
            {/* Progress */}
            <div
              className="mb-6 h-1 w-full overflow-hidden rounded-full"
              style={{ backgroundColor: "var(--wt-neutral)" }}
              data-testid="inquiry-progress"
            >
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${(step / TOTAL_STEPS) * 100}%`,
                  backgroundColor: "var(--wt-primary)",
                }}
              />
            </div>

            {step === 1 && (
              <StepShell
                step={1}
                total={TOTAL_STEPS}
                title="Your name and role."
                description="Start with how I should address you and where you sit in the work."
                onBack={null}
                onNext={goNext}
                nextDisabled={!validStep}
              >
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="full_name">
                      <span>Full name</span><LabelTag />
                    </label>
                    <input id="full_name" data-testid="inquiry-input-full_name" className="wt-input" value={form.full_name} onChange={set("full_name")} autoFocus />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="role_or_title"><span>Role or title</span><LabelTag kind="optional" /></label>
                    <input id="role_or_title" data-testid="inquiry-input-role" className="wt-input" value={form.role_or_title} onChange={set("role_or_title")} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="wt-label" htmlFor="email"><span>Email address</span><LabelTag /></label>
                      <input id="email" type="email" data-testid="inquiry-input-email" className="wt-input" value={form.email} onChange={set("email")} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="wt-label" htmlFor="phone_number"><span>Phone number</span><LabelTag kind="optional" /></label>
                      <input id="phone_number" data-testid="inquiry-input-phone" className="wt-input" value={form.phone_number} onChange={set("phone_number")} />
                    </div>
                  </div>
                </div>
              </StepShell>
            )}

            {step === 2 && (
              <StepShell
                step={2}
                total={TOTAL_STEPS}
                title="Who are you representing?"
                description="Pick the path that matches your work and share the organization name."
                onBack={goBack}
                onNext={goNext}
                nextDisabled={!validStep}
              >
                <div className="flex flex-col gap-5">
                  <div>
                    <p className="wt-label"><span>Client type</span><LabelTag /></p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {CLIENT_TYPES.map((t) => {
                        const active = form.client_type === t;
                        return (
                          <button
                            type="button"
                            key={t}
                            onClick={() => setForm((f) => ({ ...f, client_type: t }))}
                            data-testid={`inquiry-client-type-${t.toLowerCase()}`}
                            className="px-4 h-10 rounded-full text-sm font-semibold border transition-colors"
                            style={{
                              backgroundColor: active ? "var(--wt-primary)" : "#fff",
                              color: active ? "var(--wt-bg)" : "var(--wt-text)",
                              borderColor: active ? "var(--wt-primary)" : "var(--wt-border)",
                            }}
                          >
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="organization_name">
                      <span>{form.client_type === "District" ? "District name" : form.client_type === "School" ? "School name" : "Organization name"}</span>
                      <LabelTag />
                    </label>
                    <input id="organization_name" data-testid="inquiry-input-organization" className="wt-input" value={form.organization_name} onChange={set("organization_name")} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="organization_link"><span>Website or social link</span><LabelTag kind="optional" /></label>
                    <input id="organization_link" placeholder="https://" data-testid="inquiry-input-link" className="wt-input" value={form.organization_link} onChange={set("organization_link")} />
                  </div>
                </div>
              </StepShell>
            )}

            {step === 3 && (
              <StepShell
                step={3}
                total={TOTAL_STEPS}
                title="What do you need, and what is the pain point?"
                description="Map your need to a service area and describe what is happening in real terms."
                onBack={goBack}
                onNext={goNext}
                nextDisabled={!validStep}
              >
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="service_area"><span>Service area</span><LabelTag /></label>
                    <select id="service_area" data-testid="inquiry-select-service" className="wt-input" value={form.service_area} onChange={set("service_area")}>
                      <option value="">Select a service area</option>
                      {SERVICE_AREA_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="needs_and_pain_points"><span>What are you trying to solve or build?</span><LabelTag /></label>
                    <textarea id="needs_and_pain_points" rows={5} data-testid="inquiry-textarea-needs" className="wt-input" value={form.needs_and_pain_points} onChange={set("needs_and_pain_points")} />
                  </div>
                </div>
              </StepShell>
            )}

            {step === 4 && (
              <StepShell
                step={4}
                total={TOTAL_STEPS}
                title="Scale and timeline."
                description="Help me understand the size of the room and when this needs to land."
                onBack={goBack}
                onNext={goNext}
                nextDisabled={!validStep}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="scale_or_audience_size"><span>Scale or audience size</span><LabelTag kind="optional" /></label>
                    <input id="scale_or_audience_size" data-testid="inquiry-input-scale" className="wt-input" value={form.scale_or_audience_size} onChange={set("scale_or_audience_size")} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="timeline"><span>Timeline</span><LabelTag kind="optional" /></label>
                    <select id="timeline" data-testid="inquiry-select-timeline" className="wt-input" value={form.timeline} onChange={set("timeline")}>
                      <option value="">Select a timeline</option>
                      {TIMELINES.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </StepShell>
            )}

            {step === 5 && (
              <StepShell
                step={5}
                total={TOTAL_STEPS}
                title="Budget range."
                description="This is the only place we ask about budget. A range is fine. It helps me prepare and respect your time."
                onBack={goBack}
                onNext={goNext}
                nextDisabled={!validStep}
              >
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="budget_range"><span>Budget range</span><LabelTag /></label>
                    <select id="budget_range" data-testid="inquiry-select-budget" className="wt-input" value={form.budget_range} onChange={set("budget_range")}>
                      {BUDGET_RANGES.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="holds_budget"><span>Do you hold the budget</span><LabelTag kind="optional" /></label>
                    <select id="holds_budget" data-testid="inquiry-select-holds-budget" className="wt-input" value={form.holds_budget} onChange={set("holds_budget")}>
                      <option value="">Select</option>
                      {HOLDS_BUDGET_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </StepShell>
            )}

            {step === 6 && (
              <StepShell
                step={6}
                total={TOTAL_STEPS}
                title="How did you hear about Wanakee?"
                description="This helps prioritize where to invest energy. Optional."
                onBack={goBack}
                onNext={goNext}
                nextDisabled={!validStep}
              >
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <label className="wt-label" htmlFor="how_heard"><span>Source</span><LabelTag kind="optional" /></label>
                    <select id="how_heard" data-testid="inquiry-select-source" className="wt-input" value={form.how_heard} onChange={set("how_heard")}>
                      <option value="">Select</option>
                      {REFERRAL_SOURCES.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  {form.how_heard && (
                    <div className="flex flex-col gap-2">
                      <label className="wt-label" htmlFor="how_heard_detail"><span>A little more detail</span><LabelTag kind="optional" /></label>
                      <textarea id="how_heard_detail" rows={3} data-testid="inquiry-textarea-source-detail" className="wt-input" value={form.how_heard_detail} onChange={set("how_heard_detail")} />
                    </div>
                  )}
                </div>
              </StepShell>
            )}

            {step === 7 && (
              <StepShell
                step={7}
                total={TOTAL_STEPS}
                title="One quick check."
                description="Confirm you are human, then submit. Your responses will route to the correct path inside the workspace."
                onBack={goBack}
                onNext={goNext}
                nextDisabled={!validStep}
                nextLabel={submitting ? "Submitting…" : "Submit Inquiry"}
                submitting={submitting}
              >
                <div className="hidden" aria-hidden="true">
                  <input value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" data-testid="inquiry-honeypot" />
                </div>
                <div className="flex flex-col gap-2 max-w-xs">
                  <label className="wt-label" htmlFor="captcha"><span>{`What is ${captcha.a} + ${captcha.b}?`}</span><LabelTag /></label>
                  <input id="captcha" type="number" inputMode="numeric" data-testid="inquiry-captcha" className="wt-input" value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} />
                </div>
              </StepShell>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
